import { useToast } from '@chakra-ui/react';
import { useListingforMlsListingLazyQuery } from '@client/graphql/__generated__/main-operations';
import { useNavigate } from 'react-router';
import { getListingReportRoute } from '~/apps/consumer/utils/routes.utils';
import { EmbeddedConfig } from '~/apps/embedded/embedded-config';
import { useCreateBuyerOfferAnalysis } from '../../BuyerOfferAnalysis/hooks/useCreateBuyerOfferAnalysis';
import { MlsListingWithListing } from '../ExploreContext';

interface UseMapCardActionsProps {
  isEmbedded?: boolean;
  utmParams?: Record<string, string>;
}

const applyUtmParamAndEmbedded = (
  route: string,
  isEmbedded: boolean,
  utmParams?: Record<string, string>
) => {
  const baseRoute = isEmbedded ? `${EmbeddedConfig.baseUrl}${route}` : route;

  const routeWithUtm = utmParams
    ? `${baseRoute}?${new URLSearchParams(utmParams).toString()}`
    : baseRoute;

  return routeWithUtm;
};

export const useMapCardActions = ({
  isEmbedded = false,
  utmParams,
}: UseMapCardActionsProps) => {
  const navigate = useNavigate();
  const [listingForMlsListing] = useListingforMlsListingLazyQuery();
  const toast = useToast();
  const { handleCreate, loading } = useCreateBuyerOfferAnalysis();

  const fetchPropertySlugAndNavigate = async ({
    mlsListing,
  }: MlsListingWithListing) => {
    try {
      const { data } = await listingForMlsListing({
        variables: {
          input: {
            id: mlsListing?.id,
          },
        },
      });

      if (data?.syncListingFromMLS?.property?.slug) {
        const slug = data.syncListingFromMLS.property.slug;
        const listingReportRoute = getListingReportRoute(slug);
        const route = applyUtmParamAndEmbedded(
          listingReportRoute,
          isEmbedded,
          utmParams
        );

        navigate(route);
      } else {
        toast({
          title: 'Could not find property',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Could not find property',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const onCardClick = ({ listing, mlsListing }: MlsListingWithListing) => {
    if (listing?.property?.slug) {
      const listingReportRoute = getListingReportRoute(listing.property.slug);
      const route = applyUtmParamAndEmbedded(
        listingReportRoute,
        isEmbedded,
        utmParams
      );

      navigate(route);
    } else if (!listing?.property?.slug && mlsListing) {
      void fetchPropertySlugAndNavigate({ mlsListing });
    }
  };

  const onStartOfferScorecardClick = async ({
    listing,
    mlsListing,
  }: MlsListingWithListing) => {
    if (!listing?.id && !mlsListing?.id) {
      return;
    }

    const boa = await handleCreate({
      listingId: listing?.id,
      mlsListingId: mlsListing?.id,
    });
    if (boa?.id) {
      navigate(`/offer-scorecard/${boa.id}`);
    }
  };

  return {
    onCardClick,
    onStartOfferScorecardClick,
    isCreatingBOA: loading,
  };
};
